import { Download, ExternalLink } from 'lucide-react';

import type { SignInHelperLinks } from '../config/consoleConfig';
import { ACCENT, IDENTIFIER, TEXT } from '../theme/tokens';

// FD-610 — the sign-in helper's install affordance. Used by the Activate page's
// Sign-in helper check (FD-592/FD-608); since FD-612 the activation dialog uses
// `SignInHelperCard`, which shares `ManualInstallSteps` and the same config rules.
// FD-610's original reason, kept for the record: the dialog once said only "the
// helper removes that step", so an admin who activated from the campaign Overview
// never learned how to get it.
//
// Same config rules everywhere: store links first, the interim download second (or
// alone when no store listing exists), and "from your administrator" when nothing is
// configured — never a link that goes nowhere.
//
// ADVISORY. Nothing here gates activation.

/** Store links first, the interim download second; "from your administrator" when neither. */
export function HelperInstallLinks({ links }: { links: SignInHelperLinks | undefined }) {
  const hasStore = Boolean(links?.edgeStoreUrl || links?.chromeStoreUrl);
  const downloadUrl = links?.downloadUrl;

  const downloadLink = downloadUrl ? (
    <a
      href={downloadUrl}
      download
      className="inline-flex items-center gap-1 underline"
      style={{ color: ACCENT.pressed }}
      data-testid="helper-download"
    >
      <Download size={12} strokeWidth={1.5} />
      {hasStore ? 'Or download the interim build' : 'Download the helper'}
    </a>
  ) : null;

  if (hasStore) {
    return (
      <span className="inline-flex flex-wrap items-center gap-3" data-testid="helper-install-links">
        {links?.edgeStoreUrl && (
          <a
            href={links.edgeStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline"
            style={{ color: ACCENT.pressed }}
          >
            Get it for Microsoft Edge <ExternalLink size={12} strokeWidth={1.5} />
          </a>
        )}
        {links?.chromeStoreUrl && (
          <a
            href={links.chromeStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 underline"
            style={{ color: ACCENT.pressed }}
          >
            Get it for Google Chrome <ExternalLink size={12} strokeWidth={1.5} />
          </a>
        )}
        {downloadLink}
      </span>
    );
  }
  if (downloadLink) {
    return (
      <span className="inline-flex flex-wrap items-center gap-3" data-testid="helper-install-links">
        {downloadLink}
      </span>
    );
  }
  return (
    <span data-testid="helper-from-admin">
      The AirBridge sign-in helper is available from your administrator.
    </span>
  );
}

export function ManualInstallSteps() {
  return (
    <div className="flex flex-col gap-1.5 text-[12.5px] leading-snug" style={{ color: TEXT.secondary }} data-testid="helper-manual-steps">
      <ol className="m-0 list-decimal pl-5">
        <li>Unzip the download to a folder you will keep (the browser loads it from there).</li>
        <li>
          Open <span style={IDENTIFIER}>edge://extensions</span> in Microsoft Edge, or{' '}
          <span style={IDENTIFIER}>chrome://extensions</span> in Google Chrome.
        </li>
        <li>Turn on Developer mode.</li>
        <li>Choose Load unpacked, then select the unzipped folder.</li>
        <li>Come back to this tab. It picks the helper up by itself; use Check again if it does not.</li>
      </ol>
      <p className="m-0">
        This is an interim install until the store listing is available. Because it is loaded
        in Developer mode, the browser may warn about developer-mode extensions, and IT policy
        can block it.
      </p>
    </div>
  );
}
